(() => {
  const sectionSelector = '.atlas-reviews';
  const widgetSelector = '.atlas-reviews__widget';
  const carouselSelector = '.reputon-carousel-holder';
  const cardSelector = '.reputon-card';
  const focusableSelector = 'a[href], button, input, select, textarea, [tabindex]';
  const originalTabindex = new WeakMap();
  const managedSlides = new WeakSet();
  const managedFocusables = new WeakSet();
  const currentSlidesByHolder = new WeakMap();

  function isRendered(element) {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return rect.width > 0
      && rect.height > 0
      && style.display !== 'none'
      && style.visibility !== 'hidden';
  }

  function rememberTabindex(element) {
    if (!originalTabindex.has(element)) {
      originalTabindex.set(element, element.getAttribute('tabindex'));
    }
  }

  function removeFromTabOrder(element) {
    rememberTabindex(element);
    managedFocusables.add(element);
    element.setAttribute('tabindex', '-1');
  }

  function restoreTabOrder(element) {
    if (!managedFocusables.has(element)) return;

    const tabindex = originalTabindex.get(element);
    if (tabindex === null) {
      element.removeAttribute('tabindex');
    } else {
      element.setAttribute('tabindex', tabindex);
    }
    managedFocusables.delete(element);
  }

  function getSlides(holder) {
    const swiperSlides = Array.from(holder.querySelectorAll('.swiper-slide'));
    if (swiperSlides.length) return swiperSlides;

    const topLevelCards = Array.from(holder.querySelectorAll(`.reputon-single-review${cardSelector}`)).filter(
      (card) => !card.parentElement.closest(`.reputon-single-review${cardSelector}`),
    );
    return Array.from(new Set(topLevelCards.map((card) => card.parentElement)));
  }

  function isSlideVisible(slide, holder) {
    const slideRect = slide.getBoundingClientRect();
    const holderRect = holder.getBoundingClientRect();

    return isRendered(slide)
      && slideRect.right > holderRect.left + 1
      && slideRect.left < holderRect.right - 1
      && slideRect.bottom > holderRect.top + 1
      && slideRect.top < holderRect.bottom - 1;
  }

  function prepareNavigationControls(widget) {
    widget.querySelectorAll('.reputon-swiper-button-prev, .reputon-swiper-button-next').forEach((control) => {
      const isPrevious = control.classList.contains('reputon-swiper-button-prev');
      const isNativeControl = control.matches('button, a[href]');

      if (!control.hasAttribute('aria-label')) {
        control.setAttribute('aria-label', isPrevious ? 'Previous slide' : 'Next slide');
      }

      if (!isNativeControl) {
        control.setAttribute('role', 'button');
        control.setAttribute('tabindex', isRendered(control) ? '0' : '-1');

        if (control.dataset.atlasKeyboardControl !== 'true') {
          control.dataset.atlasKeyboardControl = 'true';
          control.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            control.click();
          });
        }
      }
    });
  }

  function restoreSlide(slide) {
    if (managedSlides.has(slide)) {
      slide.removeAttribute('inert');
      slide.removeAttribute('aria-hidden');
      managedSlides.delete(slide);
    }

    slide.querySelectorAll(focusableSelector).forEach((element) => {
      if (isRendered(element)) {
        restoreTabOrder(element);
      } else {
        removeFromTabOrder(element);
      }
    });
  }

  function hideSlide(slide, holder) {
    const activeElement = document.activeElement;
    if (activeElement && slide.contains(activeElement)) {
      const widget = holder.closest(widgetSelector);
      const controls = widget
        ? Array.from(widget.querySelectorAll('.reputon-swiper-button-next, .reputon-swiper-button-prev'))
        : [];
      const safeTarget = holder.closest(sectionSelector)?.querySelector('.atlas-reviews__cta')
        || controls.find(isRendered);
      safeTarget?.focus({ preventScroll: true });
    }

    slide.querySelectorAll(focusableSelector).forEach(removeFromTabOrder);
    if (slide.getAttribute('aria-hidden') !== 'true') slide.setAttribute('aria-hidden', 'true');
    if (!slide.hasAttribute('inert')) slide.setAttribute('inert', '');
    managedSlides.add(slide);
  }

  function updateCarousel(holder) {
    const slides = getSlides(holder);
    const slideSet = new Set(slides);
    const widget = holder.closest(widgetSelector);
    const regionHeading = holder.closest('.atlas-reviews__feed')?.querySelector('.atlas-reviews__platform-heading');

    currentSlidesByHolder.get(holder)?.forEach((slide) => {
      if (slideSet.has(slide)) return;
      restoreSlide(slide);
      slide.removeAttribute('role');
      slide.removeAttribute('aria-roledescription');
      slide.removeAttribute('aria-label');
    });
    currentSlidesByHolder.set(holder, slideSet);

    holder.setAttribute('role', 'group');
    holder.setAttribute('aria-roledescription', 'carousel');
    if (regionHeading?.textContent.trim()) {
      holder.setAttribute('aria-label', regionHeading.textContent.trim());
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Review ${index + 1} of ${slides.length}`);

      if (isSlideVisible(slide, holder)) {
        restoreSlide(slide);
      } else {
        hideSlide(slide, holder);
      }
    });

    if (widget) prepareNavigationControls(widget);

    widget?.querySelectorAll(focusableSelector).forEach((element) => {
      const slide = element.closest('[aria-roledescription="slide"]');
      if (slide) return;

      if (isRendered(element)) {
        restoreTabOrder(element);
      } else {
        removeFromTabOrder(element);
      }
    });
  }

  function initialiseCarousel(holder) {
    if (holder.dataset.atlasAccessibilityReady === 'true') return;
    holder.dataset.atlasAccessibilityReady = 'true';

    let updateQueued = false;
    const queueUpdate = () => {
      if (updateQueued) return;
      updateQueued = true;
      window.requestAnimationFrame(() => {
        updateQueued = false;
        updateCarousel(holder);
      });
    };

    new MutationObserver(queueUpdate).observe(holder, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
      childList: true,
      subtree: true,
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(queueUpdate).observe(holder);
    }

    queueUpdate();
  }

  function initialiseSection(section) {
    section.querySelectorAll(widgetSelector).forEach((widget) => {
      const initialiseWidgets = () => widget.querySelectorAll(carouselSelector).forEach(initialiseCarousel);
      initialiseWidgets();

      if (widget.dataset.atlasAccessibilityObserver !== 'true') {
        widget.dataset.atlasAccessibilityObserver = 'true';
        new MutationObserver(initialiseWidgets).observe(widget, { childList: true, subtree: true });
      }
    });
  }

  function initialise(root = document) {
    if (root.matches?.(sectionSelector)) initialiseSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initialiseSection);
  }

  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
